import React, { FC } from "react";
import { useQuery, gql } from "@apollo/client";
import useCoinPricingExperiment from "./useCoinPricingExperiment";
import isServer from "../utils/isServer";

interface PageDefaults {
  freeSendCap: number;
  freeSendCapPerRecipientCoinPrice: number;
}

interface CoinPackage {
  id: string;
  coins: number;
  prices: {
    AUD: number;
    GBP: number;
    USD: number;
  };
}

interface NavNode {
  color?: string;
  childNodes: NavNode[];
  customUrl: string;
  id: string;
  promos: any[];
  slug: string;
  title: string;
  type: string;
}

interface State {
  pageDefaults: PageDefaults;
  coinPackages: CoinPackage[];
  navNodes: NavNode[];
}

interface Props {
  children: React.ReactNode;
}

const PAGE_DEFAULTS_AND_NAVNODES_QUERY = gql`
  fragment ChildNavNode on NavNode {
    color
    customUrl
    title: fullTitle
    id
    type
    slug
  }

  query getPageDefaultsNavNodes {
    pageDefaults {
      freeSendCap
      freeSendCapPerRecipientCoinPrice
      premiumOpenShareCoinPrice
    }
    navNodes {
      ...ChildNavNode
      promos {
        imageAlt
        imageSrc
        linkUrl
      }
      childNodes {
        ...ChildNavNode
        childNodes {
          ...ChildNavNode
          childNodes {
            ...ChildNavNode
          }
        }
      }
    }
  }
`;

const initialPageDefaults = {
  freeSendCap: 50,
  freeSendCapPerRecipientCoinPrice: 1,
};

interface CoinPackage {
  id: string;
  coins: number;
  prices: {
    AUD: number;
    GBP: number;
    USD: number;
  };
}

const COINS_QUERY = gql`
    query getCoinPackages($clientPlatform: ClientPlatform, $currency: Currency, $defaultForType: Boolean, $listName: String) {
        coinPackages: products(
            clientPlatform: $clientPlatform
            currency: $currency
            types: [coin_product]
            defaultForType: $defaultForType
            listName: $listName
        ) {
            id
            name
            coins
            prices {
                USD
                GBP
                AUD
            }
        }
    }
`;

const initialCoinPackages = [
  {
    id: "1",
    coins: 25,
    prices: {
      AUD: 1600,
      GBP: 800,
      USD: 1000,
    },
  },
  {
    id: "2",
    coins: 100,
    prices: {
      AUD: 3200,
      GBP: 1800,
      USD: 2000,
    },
  },
  {
    id: "3",
    coins: 200,
    prices: {
      AUD: 440,
      GBP: 2800,
      USD: 3000,
    },
  },
  {
    id: "4",
    coins: 400,
    prices: {
      AUD: 7200,
      GBP: 4600,
      USD: 5000,
    },
  },
  {
    id: "5",
    coins: 1000,
    prices: {
      AUD: 15000,
      GBP: 9000,
      USD: 10000,
    },
  },
];

const initialNavNodes: NavNode[] = [];

const initialState = {
  pageDefaults: initialPageDefaults,
  coinPackages: initialCoinPackages,
  navNodes: initialNavNodes,
};

export const PageDefaultsContext = React.createContext<State>(initialState);

export const PageDefaults: FC<Props> = React.memo(({ children }) => {
  const listName = useCoinPricingExperiment();
  const { loading, data } = useQuery(PAGE_DEFAULTS_AND_NAVNODES_QUERY);
  const { data: data2 } = useQuery(COINS_QUERY, {
    variables: { clientPlatform: "web", currency: "USD", defaultForType: listName === 'default', listName },
    skip: !listName || isServer(),
  });

  if (loading) return null;

  const pageDefaults = data?.pageDefaults || initialPageDefaults;
  const coinPackages = (data2 && data2.coinPackages && data2.coinPackages.length) ? data2.coinPackages : initialCoinPackages;
  const navNodes = data?.navNodes || initialNavNodes;

  return (
    <PageDefaultsContext.Provider
      value={{ pageDefaults, coinPackages, navNodes }}
    >
      {children}
    </PageDefaultsContext.Provider>
  );
});

function usePageDefaults() {
  return React.useContext(PageDefaultsContext);
}

export default usePageDefaults;
